(() => {
  const SECTION_1_SELECTOR = '[handle="section1"]';
  const BUTTON_SELECTOR =
    '[handle="container1"] [handle="container1-1"] [handle="button1"]';
  const SECTION_2_SELECTOR = '[handle="section2"]';
  const TARGET_SELECTOR = ':scope > [handle="container"]';
  const EXPANDED_ATTRIBUTE = "data-deer-section2-expanded";
  let isExpanded = false;

  const getButtons = () =>
    Array.from(
      document.querySelectorAll(`${SECTION_1_SELECTOR} ${BUTTON_SELECTOR}`),
    );

  const syncState = () => {
    const section2 = document.querySelector(SECTION_2_SELECTOR);
    if (section2) {
      section2.setAttribute(EXPANDED_ATTRIBUTE, String(isExpanded));
    }

    getButtons().forEach((button) => {
      button.setAttribute("aria-expanded", String(isExpanded));
    });
  };

  const revealSection2 = () => {
    const section2 = document.querySelector(SECTION_2_SELECTOR);
    const target = section2?.querySelector(TARGET_SELECTOR);
    if (!section2 || !target) return;

    isExpanded = true;
    syncState();

    window.requestAnimationFrame(() => {
      target.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
        block: "start",
      });
    });
  };

  const handleClick = (event) => {
    const button = event.target?.closest?.(BUTTON_SELECTOR);
    if (!button || !button.closest(SECTION_1_SELECTOR)) return;

    event.preventDefault();
    revealSection2();
  };

  document.addEventListener("click", handleClick);
  document.addEventListener("shopify:section:load", syncState);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", syncState, { once: true });
  } else {
    syncState();
  }
})();

// Keep Section2 quiz answers on one line in a two-column desktop layout and
// switch the whole option list to one column whenever an answer would clip.
(() => {
  const ROOT_SELECTOR =
    '[handle="section2" i] [handle="qa-card" i]';
  const OPTION_SELECTOR = 'button[aria-pressed]';
  const LABEL_SELECTOR = ".truncate";
  const LAYOUT_ATTRIBUTE = "data-deer-qa-answer-layout";

  let animationFrame = 0;
  const observedRoots = new WeakSet();
  const resizeObserver =
    typeof ResizeObserver === "function"
      ? new ResizeObserver(() => scheduleUpdate())
      : null;

  const getGap = (element, fallback = 0) => {
    const styles = window.getComputedStyle(element);
    const value = Number.parseFloat(styles.columnGap || styles.gap);
    return Number.isFinite(value) ? value : fallback;
  };

  const getRequiredButtonWidth = (button) => {
    const styles = window.getComputedStyle(button);
    const children = Array.from(button.children);
    const horizontalPadding =
      (Number.parseFloat(styles.paddingLeft) || 0) +
      (Number.parseFloat(styles.paddingRight) || 0);
    const horizontalBorders =
      (Number.parseFloat(styles.borderLeftWidth) || 0) +
      (Number.parseFloat(styles.borderRightWidth) || 0);
    const childrenWidth = children.reduce((total, child) => {
      const childRect = child.getBoundingClientRect();
      const label = child.querySelector(LABEL_SELECTOR);
      const hiddenLabelWidth = label
        ? Math.max(0, label.scrollWidth - label.clientWidth)
        : 0;
      return total + Math.max(childRect.width, child.scrollWidth) + hiddenLabelWidth;
    }, 0);

    return (
      horizontalPadding +
      horizontalBorders +
      childrenWidth +
      getGap(button) * Math.max(0, children.length - 1)
    );
  };

  const getOptionLists = (root) => {
    const lists = new Set();
    root.querySelectorAll(OPTION_SELECTOR).forEach((button) => {
      const parent = button.parentElement;
      if (!(parent instanceof HTMLDivElement)) return;
      const directOptions = Array.from(parent.children).filter((child) =>
        child.matches(OPTION_SELECTOR),
      );
      if (directOptions.length > 1) lists.add(parent);
    });
    return Array.from(lists);
  };

  const updateList = (list) => {
    const options = Array.from(list.children).filter((child) =>
      child.matches(OPTION_SELECTOR),
    );
    if (!list.clientWidth || options.length < 2) {
      list.removeAttribute(LAYOUT_ATTRIBUTE);
      return;
    }

    const twoColumnWidth = (list.clientWidth - getGap(list, 10)) / 2;
    const needsSingleColumn = options.some(
      (option) => getRequiredButtonWidth(option) > twoColumnWidth + 0.5,
    );

    if (needsSingleColumn) {
      list.setAttribute(LAYOUT_ATTRIBUTE, "single");
    } else {
      list.setAttribute(LAYOUT_ATTRIBUTE, "two");
    }
  };

  const updateAll = () => {
    animationFrame = 0;
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (resizeObserver && !observedRoots.has(root)) {
        observedRoots.add(root);
        resizeObserver.observe(root);
      }
      getOptionLists(root).forEach(updateList);
    });
  };

  function scheduleUpdate() {
    if (animationFrame) return;
    animationFrame = window.requestAnimationFrame(updateAll);
  }

  const start = () => {
    scheduleUpdate();

    if (document.body && typeof MutationObserver === "function") {
      const mutationObserver = new MutationObserver(scheduleUpdate);
      mutationObserver.observe(document.body, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    document.fonts?.ready.then(scheduleUpdate);
    document.fonts?.addEventListener?.("loadingdone", scheduleUpdate);
  };

  window.addEventListener("resize", scheduleUpdate, { passive: true });
  document.addEventListener("shopify:section:load", scheduleUpdate);
  document.addEventListener("shopify:block:select", scheduleUpdate);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})();

(() => {
  const SECTION_SELECTOR = '[handle="section10" i]';
  const BLOG_WRAPPER_SELECTOR = '[handle="blog-wrapper" i]';
  const CARD_SELECTOR = ".article-card[data-embla-slide]";
  const READ_MORE_SELECTOR = '[handle="blog-link" i]';
  const READY_ATTRIBUTE = "data-deer-section10-blog-ready";
  const DRAGGING_ATTRIBUTE = "data-deer-section10-blog-dragging";
  const PROGRESS_CLASS = "deer-section10-blog-progress";
  const PROGRESS_THUMB_CLASS = "deer-section10-blog-progress__thumb";
  const cleanupBySection = new Map();

  const createProgress = () => {
    const progress = document.createElement("div");
    progress.className = PROGRESS_CLASS;
    progress.setAttribute("role", "progressbar");
    progress.setAttribute("aria-label", "Blog carousel progress");
    progress.setAttribute("aria-valuemin", "0");
    progress.setAttribute("aria-valuemax", "100");
    progress.setAttribute("aria-valuenow", "0");

    const thumb = document.createElement("div");
    thumb.className = PROGRESS_THUMB_CLASS;
    progress.append(thumb);

    return { progress, thumb };
  };

  const getCards = (wrapper) =>
    Array.from(wrapper.children).filter((child) =>
      child.matches(CARD_SELECTOR),
    );

  const bindReadMoreLinks = (wrapper) => {
    const cleanups = [];

    wrapper.querySelectorAll(READ_MORE_SELECTOR).forEach((link) => {
      const card = link.closest(CARD_SELECTOR);
      const href = card?.getAttribute("data-article-url")?.trim();
      if (!href) return;

      if (link instanceof HTMLAnchorElement) {
        if (!link.getAttribute("href")) link.setAttribute("href", href);
        return;
      }

      const originalRole = link.getAttribute("role");
      const originalTabIndex = link.getAttribute("tabindex");
      const handleClick = (event) => {
        event.preventDefault();
        window.location.assign(href);
      };
      const handleKeydown = (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        window.location.assign(href);
      };

      link.setAttribute("role", "link");
      link.setAttribute("tabindex", "0");
      link.addEventListener("click", handleClick);
      link.addEventListener("keydown", handleKeydown);
      cleanups.push(() => {
        link.removeEventListener("click", handleClick);
        link.removeEventListener("keydown", handleKeydown);
        if (originalRole === null) link.removeAttribute("role");
        else link.setAttribute("role", originalRole);
        if (originalTabIndex === null) link.removeAttribute("tabindex");
        else link.setAttribute("tabindex", originalTabIndex);
      });
    });

    return cleanups;
  };

  const initializeSection = (section) => {
    if (cleanupBySection.has(section)) return;

    const wrapper = section.querySelector(BLOG_WRAPPER_SELECTOR);
    if (!(wrapper instanceof HTMLElement)) return;

    const cards = getCards(wrapper);
    if (!cards.length) return;
    const readMoreCleanups = bindReadMoreLinks(wrapper);

    const { progress, thumb } = createProgress();
    const originalAttributes = {
      ariaLabel: wrapper.getAttribute("aria-label"),
      role: wrapper.getAttribute("role"),
      tabindex: wrapper.getAttribute("tabindex"),
    };
    const originalScrollBehavior = wrapper.style.scrollBehavior;
    wrapper.insertAdjacentElement("afterend", progress);
    wrapper.setAttribute(READY_ATTRIBUTE, "true");
    wrapper.setAttribute("tabindex", "0");
    wrapper.setAttribute("role", "region");
    wrapper.setAttribute("aria-label", "Blog articles");

    let updateFrame = 0;
    let pointerId = null;
    let pointerStartX = 0;
    let pointerStartScroll = 0;
    let dragged = false;

    const updateProgress = () => {
      window.cancelAnimationFrame(updateFrame);
      updateFrame = window.requestAnimationFrame(() => {
        const maxScroll = Math.max(0, wrapper.scrollWidth - wrapper.clientWidth);
        const scrollProgress =
          maxScroll > 1
            ? Math.max(0, Math.min(1, wrapper.scrollLeft / maxScroll))
            : 0;
        const snapCount = Math.max(1, getCards(wrapper).length);
        const normalized =
          (scrollProgress * Math.max(1, snapCount - 1) + 1) / snapCount;

        progress.hidden = maxScroll <= 1;
        thumb.style.width = `${Math.max(0, Math.min(1, normalized)) * 100}%`;
        progress.setAttribute(
          "aria-valuenow",
          String(Math.round(scrollProgress * 100)),
        );
      });
    };

    const scrollByCard = (direction) => {
      const currentCards = getCards(wrapper);
      const firstCard = currentCards[0];
      const computedStyle = window.getComputedStyle(wrapper);
      const gap =
        Number.parseFloat(computedStyle.columnGap || computedStyle.gap) || 0;
      const step = (firstCard?.getBoundingClientRect().width || wrapper.clientWidth) + gap;
      wrapper.scrollBy({ behavior: "smooth", left: direction * step });
    };

    const handleKeydown = (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      scrollByCard(event.key === "ArrowLeft" ? -1 : 1);
    };

    const handlePointerDown = (event) => {
      if (event.pointerType !== "mouse" || event.button !== 0) return;
      const readMoreLink = event.target?.closest?.(READ_MORE_SELECTOR);
      if (readMoreLink && wrapper.contains(readMoreLink)) {
        dragged = false;
        pointerId = null;
        wrapper.removeAttribute(DRAGGING_ATTRIBUTE);
        return;
      }
      pointerId = event.pointerId;
      pointerStartX = event.clientX;
      pointerStartScroll = wrapper.scrollLeft;
      dragged = false;
      wrapper.style.scrollBehavior = "auto";
      wrapper.setPointerCapture?.(event.pointerId);
    };

    const handlePointerMove = (event) => {
      if (pointerId !== event.pointerId) return;
      const distance = event.clientX - pointerStartX;
      if (Math.abs(distance) > 4) {
        dragged = true;
        wrapper.setAttribute(DRAGGING_ATTRIBUTE, "true");
      }
      if (!dragged) return;
      event.preventDefault();
      wrapper.scrollLeft = pointerStartScroll - distance;
    };

    const handlePointerEnd = (event) => {
      if (pointerId !== event.pointerId) return;
      wrapper.releasePointerCapture?.(event.pointerId);
      pointerId = null;
      wrapper.style.scrollBehavior = originalScrollBehavior;
      wrapper.removeAttribute(DRAGGING_ATTRIBUTE);
    };

    const handlePointerCancel = (event) => {
      handlePointerEnd(event);
      dragged = false;
    };

    const handleClick = (event) => {
      const readMoreLink = event.target?.closest?.(READ_MORE_SELECTOR);
      if (readMoreLink && wrapper.contains(readMoreLink)) {
        dragged = false;
        return;
      }
      if (!dragged) return;
      event.preventDefault();
      event.stopPropagation();
      dragged = false;
    };

    const resizeObserver =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(updateProgress)
        : null;
    const mutationObserver =
      typeof MutationObserver === "function"
        ? new MutationObserver(updateProgress)
        : null;

    wrapper.addEventListener("scroll", updateProgress, { passive: true });
    wrapper.addEventListener("keydown", handleKeydown);
    wrapper.addEventListener("pointerdown", handlePointerDown);
    wrapper.addEventListener("pointermove", handlePointerMove);
    wrapper.addEventListener("pointerup", handlePointerEnd);
    wrapper.addEventListener("pointercancel", handlePointerCancel);
    wrapper.addEventListener("click", handleClick, true);
    resizeObserver?.observe(wrapper);
    mutationObserver?.observe(wrapper, { childList: true });
    updateProgress();

    cleanupBySection.set(section, () => {
      window.cancelAnimationFrame(updateFrame);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      wrapper.removeEventListener("scroll", updateProgress);
      wrapper.removeEventListener("keydown", handleKeydown);
      wrapper.removeEventListener("pointerdown", handlePointerDown);
      wrapper.removeEventListener("pointermove", handlePointerMove);
      wrapper.removeEventListener("pointerup", handlePointerEnd);
      wrapper.removeEventListener("pointercancel", handlePointerCancel);
      wrapper.removeEventListener("click", handleClick, true);
      readMoreCleanups.forEach((cleanup) => cleanup());
      wrapper.removeAttribute(READY_ATTRIBUTE);
      wrapper.removeAttribute(DRAGGING_ATTRIBUTE);
      wrapper.style.scrollBehavior = originalScrollBehavior;
      Object.entries(originalAttributes).forEach(([name, value]) => {
        const attributeName = name === "ariaLabel" ? "aria-label" : name;
        if (value === null) {
          wrapper.removeAttribute(attributeName);
        } else {
          wrapper.setAttribute(attributeName, value);
        }
      });
      progress.remove();
      cleanupBySection.delete(section);
    });
  };

  const initialize = (root = document) => {
    if (root instanceof Element && root.matches(SECTION_SELECTOR)) {
      initializeSection(root);
    }
    root.querySelectorAll?.(SECTION_SELECTOR).forEach(initializeSection);
  };

  document.addEventListener("shopify:section:load", (event) => {
    initialize(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    if (event.target instanceof Element) {
      const sections = event.target.matches(SECTION_SELECTOR)
        ? [event.target]
        : Array.from(event.target.querySelectorAll(SECTION_SELECTOR));
      sections.forEach((section) => cleanupBySection.get(section)?.());
    }
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize(), {
      once: true,
    });
  } else {
    initialize();
  }
})();

(() => {
  const SLIDE_SELECTOR =
    ':is([handle="section3" i], [handle="section8" i]) [handle="RECOMMENDED-GEAR-PAIRINGS" i] [handle="embla" i] [data-embla-slide]';
  const SAVING_SELECTOR = "[data-deer-bundle-saving]";
  const BADGE_CLASS = "deer-bundle-save-badge";

  const initializeBundleBadges = (root = document) => {
    root.querySelectorAll(SLIDE_SELECTOR).forEach((slide) => {
      const image = Array.from(slide.children).find(
        (child) => child.matches?.('[handle="image" i]'),
      );
      const saving = slide
        .querySelector(SAVING_SELECTOR)
        ?.getAttribute("data-deer-bundle-saving")
        ?.trim();
      const existingBadge = image?.querySelector(`.${BADGE_CLASS}`);

      if (!image || !saving) {
        existingBadge?.remove();
        return;
      }

      const badge = existingBadge || document.createElement("span");
      badge.className = BADGE_CLASS;
      badge.textContent = `BUNDLE & SAVE ${saving}`;
      badge.setAttribute("aria-label", `Bundle and save ${saving}`);
      if (!existingBadge) image.appendChild(badge);
    });
  };

  document.addEventListener("shopify:section:load", (event) => {
    initializeBundleBadges(event.target);
  });

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => initializeBundleBadges(),
      { once: true },
    );
  } else {
    initializeBundleBadges();
  }
})();

(() => {
  const SECTION_SELECTOR = '[handle="section3"], [handle="section8"]';
  const CARD_CONTAINER_SELECTOR = '[handle="Card"]';
  const CARD_HANDLE_PATTERN = /^container-card-(\d+)$/i;
  const DEFAULT_SECTION3_MAX_CARD_INDEX = 5;

  const ensureSectionCards = (section) => {
    const cardContainer = section.querySelector(CARD_CONTAINER_SELECTOR);
    if (!cardContainer) return;

    const cards = Array.from(cardContainer.querySelectorAll("[handle]"))
      .map((element) => {
        const match = element
          .getAttribute("handle")
          ?.match(CARD_HANDLE_PATTERN);
        return match ? { element, index: Number(match[1]) } : null;
      })
      .filter(Boolean);
    const template = cards.find(({ index }) => index === 1)?.element;
    if (!template) return;

    const maxButtonIndex = Math.max(
      -1,
      ...Array.from(section.querySelectorAll("[handle]"))
        .map((element) => {
          const match = element
            .getAttribute("handle")
            ?.match(/^related-button-(\d+)$/i);
          return match ? Number(match[1]) : -1;
        }),
    );
    const isSection3 = section.matches('[handle="section3" i]');
    const maxExistingCardIndex = Math.max(
      1,
      ...cards.map(({ index }) => index),
    );
    const maxCardIndex = Math.max(
      maxExistingCardIndex,
      maxButtonIndex,
      isSection3 ? DEFAULT_SECTION3_MAX_CARD_INDEX : 1,
    );
    const existingIndexes = new Set(cards.map(({ index }) => index));
    for (let index = 1; index <= maxCardIndex; index += 1) {
      if (existingIndexes.has(index)) continue;

      const clone = template.cloneNode(true);
      const handle = `container-Card-${index}`;
      clone.setAttribute("handle", handle);
      clone.id = handle;
      clone.dataset.deerGeneratedCard = "true";
      cardContainer.append(clone);
    }
  };

  const ensureAllSectionCards = () => {
    document.querySelectorAll(SECTION_SELECTOR).forEach(ensureSectionCards);
  };

  document.addEventListener("shopify:section:load", ensureAllSectionCards);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", ensureAllSectionCards, {
      once: true,
    });
  } else {
    ensureAllSectionCards();
  }
})();

(() => {
  const BUTTON_SELECTOR = ".related-button-hotspot";
  const MARKER_SELECTOR = ".related-button-hotspot__marker";
  const FLIPPED_ATTRIBUTE = "data-related-button-flipped";

  let animationFrame = 0;
  const observedElements = new WeakSet();
  const resizeObserver =
    typeof ResizeObserver === "function"
      ? new ResizeObserver(() => scheduleUpdate())
      : null;

  const observe = (element) => {
    if (!resizeObserver || observedElements.has(element)) return;
    observedElements.add(element);
    resizeObserver.observe(element);
  };

  const updateButton = (button) => {
    const boundary = button.parentElement;
    const marker = button.querySelector(MARKER_SELECTOR);
    if (!boundary || !marker) return;

    observe(button);
    observe(boundary);

    const boundaryRect = boundary.getBoundingClientRect();
    const markerRect = marker.getBoundingClientRect();
    const anchorX = markerRect.left + markerRect.width / 2;
    const overflowsRight =
      anchorX + button.offsetWidth > boundaryRect.right + 0.5;

    button.setAttribute(FLIPPED_ATTRIBUTE, String(overflowsRight));
  };

  const updateAll = () => {
    document.querySelectorAll(BUTTON_SELECTOR).forEach(updateButton);
  };

  function scheduleUpdate() {
    if (animationFrame) return;
    animationFrame = window.requestAnimationFrame(() => {
      animationFrame = 0;
      updateAll();
    });
  }

  const start = () => {
    scheduleUpdate();

    if (document.body && typeof MutationObserver === "function") {
      const mutationObserver = new MutationObserver(scheduleUpdate);
      mutationObserver.observe(document.body, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    document.fonts?.ready.then(scheduleUpdate);
    document.fonts?.addEventListener?.("loadingdone", scheduleUpdate);
  };

  window.addEventListener("resize", scheduleUpdate, { passive: true });
  document.addEventListener("shopify:section:load", scheduleUpdate);
  document.addEventListener("shopify:block:select", scheduleUpdate);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})();

// Comparison Card modal for image blocks in section6 and section9 product slides.
(() => {
  const MOBILE_QUERY = "(max-width: 767px)";
  const SECTION_SELECTOR = '[handle="section6" i], [handle="section9" i]';
  const PRODUCT_CONTENT_SELECTOR = '[handle="embla-product-content" i]';
  const SLIDE_SELECTOR = '[data-embla-slide], .embla-slide, [handle="embla-slide" i]';
  const TRIGGER_SELECTOR = "[id][handle]";
  const TEMPLATE_SELECTOR = "template[data-deer-comparison-template]";
  const CARD_SELECTOR = "[data-deer-comparison-card]";
  const TRIGGER_ATTRIBUTE = "data-deer-comparison-trigger";
  const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  let modal = null;
  let dialog = null;
  let viewport = null;
  let track = null;
  let previousButton = null;
  let nextButton = null;
  let closeButton = null;
  let currentTrigger = null;
  let carouselApi = null;
  let carouselUpdate = null;
  let fallbackIndex = 0;
  let fallbackMaximum = 0;
  let currentCardCount = 0;

  const getTemplate = (trigger) => {
    const section = trigger.closest(SECTION_SELECTOR);
    if (!section || !trigger.id) return null;

    return (
      Array.from(section.querySelectorAll(TEMPLATE_SELECTOR)).find(
        (template) =>
          template.dataset.deerComparisonTemplate === trigger.id &&
          template.content.querySelector(CARD_SELECTOR),
      ) || null
    );
  };

  const isValidTrigger = (trigger) =>
    trigger instanceof HTMLElement &&
    Boolean(trigger.closest(SECTION_SELECTOR)) &&
    Boolean(trigger.closest(PRODUCT_CONTENT_SELECTOR)) &&
    Boolean(trigger.closest(SLIDE_SELECTOR)) &&
    Boolean(getTemplate(trigger));

  const initializeTriggers = (root = document) => {
    const candidates = [
      ...(root instanceof Element && root.matches(TRIGGER_SELECTOR) ? [root] : []),
      ...Array.from(root.querySelectorAll?.(TRIGGER_SELECTOR) || []),
    ];

    candidates.filter(isValidTrigger).forEach((trigger) => {
      trigger.setAttribute(TRIGGER_ATTRIBUTE, "true");
      trigger.setAttribute("aria-haspopup", "dialog");
      if (!trigger.hasAttribute("aria-label")) {
        trigger.setAttribute("aria-label", "Open blind comparison");
      }
      if (trigger.tagName !== "A" && trigger.tagName !== "BUTTON") {
        trigger.setAttribute("role", "button");
        if (!trigger.hasAttribute("tabindex")) trigger.tabIndex = 0;
      }
    });
  };

  const updateFallback = () => {
    if (!track || !previousButton || !nextButton) return;
    const cards = Array.from(track.querySelectorAll(CARD_SELECTOR));
    const step = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : 0;
    track.style.transform = `translate3d(${-fallbackIndex * step}px, 0, 0)`;
    previousButton.disabled = fallbackIndex <= 0;
    nextButton.disabled = fallbackIndex >= fallbackMaximum;
  };

  const getCanMove = (direction) => {
    if (!carouselApi) return false;
    const methodNames =
      direction === "previous"
        ? ["canGoToPrev", "canScrollPrev"]
        : ["canGoToNext", "canScrollNext"];
    const methodName = methodNames.find(
      (name) => typeof carouselApi[name] === "function",
    );
    return methodName ? Boolean(carouselApi[methodName]()) : false;
  };

  const updateCarouselButtons = () => {
    if (!previousButton || !nextButton) return;
    previousButton.disabled = !getCanMove("previous");
    nextButton.disabled = !getCanMove("next");
  };

  const destroyCarousel = () => {
    if (carouselApi) {
      if (carouselUpdate) {
        carouselApi.off?.("select", carouselUpdate);
        carouselApi.off?.("reinit", carouselUpdate);
      }
      carouselApi.destroy?.();
    }
    carouselApi = null;
    carouselUpdate = null;
    fallbackIndex = 0;
    fallbackMaximum = 0;
    if (track) track.style.removeProperty("transform");
    viewport?.parentElement?.removeAttribute("data-deer-comparison-engine");
  };

  const initializeCarousel = (cardCount) => {
    destroyCarousel();
    if (!viewport || !track || !previousButton || !nextButton) return;

    const cardsPerView = window.matchMedia(MOBILE_QUERY).matches ? 2 : 3;
    fallbackMaximum = Math.max(0, cardCount - cardsPerView);
    const hasOverflow = fallbackMaximum > 0;
    viewport.parentElement?.setAttribute(
      "data-deer-comparison-overflow",
      String(hasOverflow),
    );
    if (!hasOverflow) {
      previousButton.disabled = true;
      nextButton.disabled = true;
      return;
    }

    const emblaFactory = window.EmblaCarousel;
    if (typeof emblaFactory === "function") {
      viewport.parentElement?.setAttribute(
        "data-deer-comparison-engine",
        "embla",
      );
      carouselApi = emblaFactory(viewport, {
        align: "start",
        containScroll: "trimSnaps",
        draggable: true,
        loop: false,
      });
      carouselUpdate = updateCarouselButtons;
      carouselApi.on?.("select", carouselUpdate);
      carouselApi.on?.("reinit", carouselUpdate);
      updateCarouselButtons();
      return;
    }

    viewport.parentElement?.setAttribute(
      "data-deer-comparison-engine",
      "fallback",
    );
    updateFallback();
  };

  const moveCarousel = (direction) => {
    if (carouselApi) {
      const methodNames =
        direction === "previous"
          ? ["goToPrev", "scrollPrev"]
          : ["goToNext", "scrollNext"];
      const methodName = methodNames.find(
        (name) => typeof carouselApi[name] === "function",
      );
      carouselApi[methodName]?.();
      return;
    }

    fallbackIndex = Math.max(
      0,
      Math.min(
        fallbackMaximum,
        fallbackIndex + (direction === "previous" ? -1 : 1),
      ),
    );
    updateFallback();
  };

  const getFocusableElements = () =>
    dialog
      ? Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
          (element) =>
            !element.hasAttribute("hidden") && element.getClientRects().length,
        )
      : [];

  const closeModal = () => {
    if (!modal || modal.hidden) return;
    destroyCarousel();
    modal.hidden = true;
    document.body.classList.remove("deer-comparison-modal-open");
    currentCardCount = 0;
    const trigger = currentTrigger;
    currentTrigger = null;
    trigger?.focus({ preventScroll: true });
  };

  const ensureModal = () => {
    if (modal?.isConnected) return;

    modal = document.createElement("div");
    modal.className = "deer-comparison-modal";
    modal.hidden = true;
    modal.innerHTML = `
      <div class="deer-comparison-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="deer-comparison-modal-title" aria-describedby="deer-comparison-modal-description">
        <button class="deer-comparison-modal__close" type="button" aria-label="Close comparison">&#10005;</button>
        <header class="deer-comparison-modal__header">
          <h2 class="deer-comparison-modal__title" id="deer-comparison-modal-title">FIND YOUR BEST BLIND</h2>
          <p class="deer-comparison-modal__description" id="deer-comparison-modal-description">Start with what matters most for your hunt.</p>
        </header>
        <div class="deer-comparison-modal__carousel">
          <button class="deer-comparison-modal__nav deer-comparison-modal__nav--prev" type="button" aria-label="Previous comparison">&#10094;</button>
          <div class="deer-comparison-modal__viewport">
            <div class="deer-comparison-modal__track"></div>
          </div>
          <button class="deer-comparison-modal__nav deer-comparison-modal__nav--next" type="button" aria-label="Next comparison">&#10095;</button>
        </div>
      </div>
    `;
    document.body.append(modal);

    dialog = modal.querySelector(".deer-comparison-modal__dialog");
    viewport = modal.querySelector(".deer-comparison-modal__viewport");
    track = modal.querySelector(".deer-comparison-modal__track");
    previousButton = modal.querySelector(".deer-comparison-modal__nav--prev");
    nextButton = modal.querySelector(".deer-comparison-modal__nav--next");
    closeButton = modal.querySelector(".deer-comparison-modal__close");

    closeButton.addEventListener("click", closeModal);
    previousButton.addEventListener("click", () => moveCarousel("previous"));
    nextButton.addEventListener("click", () => moveCarousel("next"));
    modal.addEventListener("click", (event) => {
      if (event.target === modal) closeModal();
    });
  };

  const openModal = (trigger) => {
    const template = getTemplate(trigger);
    if (!template) return false;

    const cards = Array.from(template.content.querySelectorAll(CARD_SELECTOR));
    if (!cards.length) return false;

    ensureModal();
    destroyCarousel();
    track.replaceChildren(...cards.map((card) => card.cloneNode(true)));
    currentTrigger = trigger;
    currentCardCount = cards.length;
    modal.hidden = false;
    document.body.classList.add("deer-comparison-modal-open");
    initializeCarousel(cards.length);
    closeButton.focus({ preventScroll: true });
    return true;
  };

  const getEventTrigger = (target) => {
    if (!(target instanceof Element)) return null;
    const trigger = target.closest(`[${TRIGGER_ATTRIBUTE}]`);
    return isValidTrigger(trigger) ? trigger : null;
  };

  document.addEventListener("click", (event) => {
    const trigger = getEventTrigger(event.target);
    if (!trigger) return;
    if (openModal(trigger)) event.preventDefault();
  });

  document.addEventListener("keydown", (event) => {
    if (modal && !modal.hidden) {
      if (event.key === "Escape") {
        event.preventDefault();
        closeModal();
        return;
      }

      if (event.key === "Tab") {
        const focusable = getFocusableElements();
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
      return;
    }

    if (event.key !== "Enter" && event.key !== " ") return;
    const trigger = getEventTrigger(event.target);
    if (!trigger) return;
    if (openModal(trigger)) event.preventDefault();
  });

  window.addEventListener("resize", () => {
    if (modal && !modal.hidden && currentCardCount) {
      initializeCarousel(currentCardCount);
    }
  });

  document.addEventListener("shopify:section:load", (event) => {
    initializeTriggers(event.target);
  });
  document.addEventListener("shopify:section:unload", (event) => {
    if (currentTrigger && event.target.contains(currentTrigger)) closeModal();
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeTriggers(), {
      once: true,
    });
  } else {
    initializeTriggers();
  }
})();

(() => {
  const SECTION_SELECTOR = '[handle="section6" i], [handle="section9" i]';
  const TABBAR_SELECTOR = '[handle="embla-product-tabbar"]';
  const TAB_SELECTOR = '[handle="embla-product-tabitem"]';
  const CONTENT_SELECTOR = '[handle="embla-product-content"][data-embla]';
  const VIEWPORT_SELECTOR = "[data-embla-viewport]";
  const CONTAINER_SELECTOR = "[data-embla-container]";
  const SLIDE_SELECTOR = "[data-embla-slide]";
  const READY_ATTRIBUTE = "data-deer-section6-ready";
  const SLIDE_ACTIVE_ATTRIBUTE = "data-deer-product-slide-active";
  const API_PROPERTY = "__tideweEmblaApi";

  const cleanupSections = new Map();
  let retryTimer = null;
  let retryAttempts = 0;

  const getDirectChild = (parent, selector) =>
    Array.from(parent?.children || []).find((child) =>
      child.matches(selector),
    ) || null;

  const getExistingApi = (root, viewport) => {
    if (root[API_PROPERTY]) return root[API_PROPERTY];

    const instances = window.theme?.EmblasInstances;
    if (!Array.isArray(instances)) return null;

    return (
      instances.find((api) => {
        const apiRoot = api?.rootNode?.();
        return apiRoot === viewport || apiRoot?.closest?.(CONTENT_SELECTOR) === root;
      }) || null
    );
  };

  const initializeSection = (section) => {
    const tabbar = section.querySelector(TABBAR_SELECTOR);
    const root = section.querySelector(CONTENT_SELECTOR);
    const viewport = root && getDirectChild(root, VIEWPORT_SELECTOR);
    const container = viewport && getDirectChild(viewport, CONTAINER_SELECTOR);

    // A product section without configured tabs remains a normal single carousel.
    if (!tabbar) return true;
    if (!root || !viewport || !container) return false;
    if (
      section.getAttribute(READY_ATTRIBUTE) === "true" &&
      tabbar.dataset.emblaTabsBound === "true"
    ) {
      return true;
    }
    if (cleanupSections.has(section)) return true;

    const tabs = Array.from(tabbar.children).filter((tab) =>
      tab.matches(TAB_SELECTOR),
    );
    const slides = Array.from(container.children).filter((slide) =>
      slide.matches(SLIDE_SELECTOR),
    );
    if (!tabs.length || tabs.length !== slides.length) return false;

    let emblaApi = getExistingApi(root, viewport);
    if (!emblaApi) {
      if (root.dataset.emblaInitialized === "true") return false;

      const emblaFactory = window.EmblaCarousel;
      if (typeof emblaFactory !== "function") return false;

      emblaApi = emblaFactory(
        viewport,
        { loop: false, draggable: false },
        [],
      );
      root[API_PROPERTY] = emblaApi;
      root.dataset.emblaInitialized = "true";
      window.theme = window.theme || {};
      window.theme.EmblasInstances = window.theme.EmblasInstances || [];
      if (!window.theme.EmblasInstances.includes(emblaApi)) {
        window.theme.EmblasInstances.push(emblaApi);
      }
    }

    const setActive = (index) => {
      const activeIndex = Math.max(
        0,
        Math.min(Number(index) || 0, tabs.length - 1),
      );

      tabbar.setAttribute("role", "tablist");
      tabs.forEach((tab, tabIndex) => {
        const isActive = tabIndex === activeIndex;
        tab.setAttribute("data-embla-active", String(isActive));
        tab.setAttribute("aria-selected", String(isActive));
        tab.setAttribute("role", "tab");
        tab.tabIndex = isActive ? 0 : -1;
      });
      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === activeIndex;
        slide.setAttribute(SLIDE_ACTIVE_ATTRIBUTE, String(isActive));
        slide.setAttribute("aria-hidden", String(!isActive));
        slide.setAttribute("role", "tabpanel");
      });
      section.setAttribute(READY_ATTRIBUTE, "true");

      const activeTab = tabs[activeIndex];
      if (!(activeTab instanceof HTMLElement)) return;

      const edgeSpace = 8;
      const tabsRect = tabbar.getBoundingClientRect();
      const activeRect = activeTab.getBoundingClientRect();
      const tabStart = activeRect.left - tabsRect.left + tabbar.scrollLeft;
      const tabEnd = tabStart + activeRect.width;
      const previousTab = tabs[Math.max(0, activeIndex - 1)];
      const previousRect = previousTab?.getBoundingClientRect();
      const contextStart = previousRect
        ? previousRect.left - tabsRect.left + tabbar.scrollLeft
        : tabStart;
      const maxScroll = Math.max(0, tabbar.scrollWidth - tabbar.clientWidth);
      const targetScrollLeft = Math.max(
        contextStart - edgeSpace,
        tabEnd - tabbar.clientWidth + edgeSpace,
      );
      tabbar.scrollLeft = Math.max(
        0,
        Math.min(maxScroll, targetScrollLeft),
      );
    };

    const handleClick = (event) => {
      const tab = event.target?.closest?.(TAB_SELECTOR);
      if (!tab || !tabbar.contains(tab)) return;

      const index = tabs.indexOf(tab);
      if (index < 0) return;

      setActive(index);
      emblaApi.goTo(index);
    };
    const syncFromEmbla = () => setActive(emblaApi.selectedSnap?.() || 0);

    tabbar.dataset.emblaTabsBound = "true";
    tabbar.addEventListener("click", handleClick);
    emblaApi.on?.("select", syncFromEmbla);
    emblaApi.on?.("reinit", syncFromEmbla);
    emblaApi.goTo(0);
    setActive(0);

    cleanupSections.set(section, () => {
      tabbar.removeEventListener("click", handleClick);
      emblaApi.off?.("select", syncFromEmbla);
      emblaApi.off?.("reinit", syncFromEmbla);
      tabbar.removeAttribute("data-embla-tabs-bound");
      cleanupSections.delete(section);
    });
    return true;
  };

  const initializeSections = () => {
    const sections = Array.from(document.querySelectorAll(SECTION_SELECTOR));
    if (!sections.length) return false;
    return sections.map(initializeSection).every(Boolean);
  };

  const scheduleInitialize = () => {
    window.clearTimeout(retryTimer);
    if (initializeSections()) return;

    retryAttempts += 1;
    if (retryAttempts > 200) return;
    retryTimer = window.setTimeout(scheduleInitialize, 50);
  };

  const reinitialize = () => {
    window.clearTimeout(retryTimer);
    Array.from(cleanupSections.values()).forEach((remove) => remove());
    retryAttempts = 0;
    scheduleInitialize();
  };

  document.addEventListener("shopify:section:load", reinitialize);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleInitialize, {
      once: true,
    });
  } else {
    scheduleInitialize();
  }
})();

// Some product slides use a product container directly on mobile. Give those
// tracks the same two-card paging behavior as the nested mobile Emblas.
(() => {
  const MOBILE_QUERY = "(max-width: 767px)";
  const SECTION_SELECTOR = '[handle="section6" i], [handle="section9" i]';
  const PRODUCT_CONTENT_SELECTOR =
    '[handle="embla-product-content" i][data-embla]';
  const OUTER_SLIDE_SELECTOR = "[data-embla-slide]";
  const PRODUCT_CONTAINER_SELECTOR = '[handle="container" i]';
  const NESTED_EMBLA_SELECTOR = '[handle="embla" i][data-embla]';
  const READY_ATTRIBUTE = "data-deer-section6-mobile-product-ready";
  const CONTROLS_CLASS = "deer-section6-product-controls";
  const PROGRESS_CLASS = "deer-section6-product-progress";
  const PROGRESS_THUMB_CLASS = "deer-section6-product-progress__thumb";
  const HIDDEN_PRODUCT_ATTRIBUTE = "data-deer-product-more-hidden";
  const PRODUCTS_EXPANDED_EVENT = "deer:section6-products-expanded";

  let cleanup = null;
  let retryTimer = 0;
  let retryAttempts = 0;

  const getDirectChild = (parent, selector) =>
    Array.from(parent?.children || []).find((child) =>
      child.matches(selector),
    ) || null;

  const createControls = () => {
    const controls = document.createElement("div");
    controls.className = CONTROLS_CLASS;
    controls.setAttribute("data-deer-section6-product-controls", "true");
    controls.innerHTML = `
      <button class="${CONTROLS_CLASS}__button" type="button" data-deer-section6-product-prev aria-label="Previous products">
        <svg viewBox="0 0 21 17" aria-hidden="true" focusable="false"><path d="M8.98126 0.709961L1.42004 8.20996L8.98126 15.71M2.39347 8.20981H20.42" fill="none" stroke="currentColor" stroke-width="2"/></svg>
      </button>
      <button class="${CONTROLS_CLASS}__button" type="button" data-deer-section6-product-next aria-label="Next products">
        <svg viewBox="0 0 21 17" aria-hidden="true" focusable="false"><path d="M11.4388 0.709961L19 8.20996L11.4388 15.71M18.0266 8.20981H0" fill="none" stroke="currentColor" stroke-width="2"/></svg>
      </button>
      <div class="${PROGRESS_CLASS}" data-deer-section6-product-progress role="progressbar" aria-label="Product carousel progress" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">
        <div class="${PROGRESS_THUMB_CLASS}" data-deer-section6-product-progress-thumb></div>
      </div>
    `;
    return controls;
  };

  const initialize = () => {
    if (!window.matchMedia(MOBILE_QUERY).matches) return false;

    if (cleanup) return true;

    const rootCleanups = [];
    const sections = Array.from(document.querySelectorAll(SECTION_SELECTOR));
    if (!sections.length) return false;

    sections.forEach((section) => {
      const productContent = section.querySelector(PRODUCT_CONTENT_SELECTOR);
      const viewport = productContent
        ? getDirectChild(productContent, "[data-embla-viewport]")
        : null;
      const outerContainer = viewport
        ? getDirectChild(viewport, "[data-embla-container]")
        : null;
      if (!outerContainer) return;

      const outerSlides = Array.from(outerContainer.children).filter((slide) =>
        slide.matches(OUTER_SLIDE_SELECTOR),
      );

      outerSlides.forEach((outerSlide) => {
      if (getDirectChild(outerSlide, NESTED_EMBLA_SELECTOR)) return;

      const container = getDirectChild(outerSlide, PRODUCT_CONTAINER_SELECTOR);
      if (!container) return;

      const getProductSlides = () =>
        Array.from(container.children).filter((slide) =>
          slide.matches(OUTER_SLIDE_SELECTOR) &&
          slide.getAttribute(HIDDEN_PRODUCT_ATTRIBUTE) !== "true",
        );
      if (!getProductSlides().length) return;

      const controls = createControls();
      const previousButton = controls.querySelector(
        "[data-deer-section6-product-prev]",
      );
      const nextButton = controls.querySelector(
        "[data-deer-section6-product-next]",
      );
      const progress = controls.querySelector(
        "[data-deer-section6-product-progress]",
      );
      const progressThumb = controls.querySelector(
        "[data-deer-section6-product-progress-thumb]",
      );
      if (!(previousButton instanceof HTMLButtonElement)) return;
      if (!(nextButton instanceof HTMLButtonElement)) return;
      if (!(progress instanceof HTMLElement)) return;
      if (!(progressThumb instanceof HTMLElement)) return;

      container.setAttribute(READY_ATTRIBUTE, "true");
      outerSlide.append(controls);

      const originalSlideStyles = new Map();
      const syncSlideWidths = () => {
        const slides = getProductSlides();
        if (!slides.length || !container.clientWidth) return;

        const computedStyle = window.getComputedStyle(container);
        const gap =
          Number.parseFloat(computedStyle.columnGap || computedStyle.gap) || 16;
        const padding =
          (Number.parseFloat(computedStyle.paddingLeft) || 0) +
          (Number.parseFloat(computedStyle.paddingRight) || 0);
        const availableWidth = Math.max(0, container.clientWidth - padding);
        const slideWidth = Math.max(0, (availableWidth - gap) / 2);

        slides.forEach((slide) => {
          if (!originalSlideStyles.has(slide)) {
            originalSlideStyles.set(slide, slide.getAttribute("style"));
          }
          slide.style.setProperty("box-sizing", "border-box");
          slide.style.setProperty("flex", `0 0 ${slideWidth}px`, "important");
          slide.style.setProperty("flex-basis", `${slideWidth}px`, "important");
          slide.style.setProperty("min-width", "0", "important");
          slide.style.setProperty("width", `${slideWidth}px`, "important");
        });

        const maxScroll = Math.max(0, container.scrollWidth - container.clientWidth);
        if (container.scrollLeft > maxScroll) {
          container.scrollLeft = maxScroll;
        }
      };

      const updateButtons = () => {
        syncSlideWidths();
        const maxScroll = Math.max(0, container.scrollWidth - container.clientWidth);
        const currentScroll = Math.max(0, container.scrollLeft);
        previousButton.disabled = currentScroll <= 1;
        nextButton.disabled = currentScroll >= maxScroll - 1;

        const scrollProgress =
          maxScroll > 1
            ? Math.max(0, Math.min(1, currentScroll / maxScroll))
            : 0;
        const pageCount = Math.max(1, Math.ceil(getProductSlides().length / 2));
        const progressWidth =
          ((scrollProgress * Math.max(1, pageCount - 1) + 1) / pageCount) *
          100;
        progressThumb.style.width = `${Math.min(100, progressWidth)}%`;
        progressThumb.style.transform = "translateX(0)";
        progress.setAttribute(
          "aria-valuenow",
          String(Math.round(scrollProgress * 100)),
        );
      };

      const scrollByPage = (direction) => {
        const maxScroll = Math.max(
          0,
          container.scrollWidth - container.clientWidth,
        );
        const computedStyle = window.getComputedStyle(container);
        const gap =
          Number.parseFloat(computedStyle.columnGap || computedStyle.gap) || 16;
        const pageStep = Math.max(1, container.clientWidth + gap);
        const currentPage = Math.round(container.scrollLeft / pageStep);
        const target = Math.max(
          0,
          Math.min(maxScroll, (currentPage + direction) * pageStep),
        );
        container.scrollBy({
          behavior: "smooth",
          left: target - container.scrollLeft,
        });
      };

      const handlePrevious = () => scrollByPage(-1);
      const handleNext = () => scrollByPage(1);
      const resizeObserver =
        typeof ResizeObserver === "function"
          ? new ResizeObserver(updateButtons)
          : null;
      const mutationObserver =
        typeof MutationObserver === "function"
          ? new MutationObserver(updateButtons)
          : null;
      previousButton.addEventListener("click", handlePrevious);
      nextButton.addEventListener("click", handleNext);
      container.addEventListener("scroll", updateButtons, { passive: true });
      const handleProductsExpanded = () =>
        window.requestAnimationFrame(updateButtons);
      container.addEventListener(
        PRODUCTS_EXPANDED_EVENT,
        handleProductsExpanded,
      );
      window.addEventListener("resize", updateButtons, { passive: true });
      resizeObserver?.observe(container);
      mutationObserver?.observe(container, { childList: true });
      updateButtons();

      rootCleanups.push(() => {
        previousButton.removeEventListener("click", handlePrevious);
        nextButton.removeEventListener("click", handleNext);
        container.removeEventListener("scroll", updateButtons);
        container.removeEventListener(
          PRODUCTS_EXPANDED_EVENT,
          handleProductsExpanded,
        );
        window.removeEventListener("resize", updateButtons);
        resizeObserver?.disconnect();
        mutationObserver?.disconnect();
        controls.remove();
        container.removeAttribute(READY_ATTRIBUTE);
        originalSlideStyles.forEach((style, slide) => {
          if (style === null) {
            slide.removeAttribute("style");
          } else {
            slide.setAttribute("style", style);
          }
        });
      });
      });
    });

    if (!rootCleanups.length) return false;
    cleanup = () => {
      rootCleanups.forEach((remove) => remove());
      cleanup = null;
    };
    return true;
  };

  const scheduleInitialize = () => {
    window.clearTimeout(retryTimer);
    if (!window.matchMedia(MOBILE_QUERY).matches || cleanup) return;
    if (initialize()) return;

    retryAttempts += 1;
    if (retryAttempts > 200) return;
    retryTimer = window.setTimeout(scheduleInitialize, 50);
  };

  const reinitialize = () => {
    window.clearTimeout(retryTimer);
    cleanup?.();
    retryAttempts = 0;
    scheduleInitialize();
  };

  const mediaQuery = window.matchMedia(MOBILE_QUERY);
  mediaQuery.addEventListener?.("change", reinitialize);
  document.addEventListener("shopify:section:load", reinitialize);
  document.addEventListener("shopify:section:unload", reinitialize);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleInitialize, {
      once: true,
    });
  } else {
    scheduleInitialize();
  }
})();

(() => {
  const DESKTOP_QUERY = "(min-width: 768px)";
  const SECTION_SELECTOR = '[handle="section3"], [handle="section8"]';
  const CARD_HANDLE_PATTERN = /^container-card-(\d+)$/i;
  const BUTTON_HANDLE_PATTERN = /^related-button-(\d+)$/i;
  const DEFAULT_CARD_INDEX = 1;
  const READY_ATTRIBUTE = "data-deer-card-switcher-ready";
  const ACTIVE_ATTRIBUTE = "data-deer-card-active";
  const BUTTON_ACTIVE_ATTRIBUTE = "data-deer-related-button-active";

  const cleanupSections = new Map();
  let retryTimer = null;
  let retryAttempts = 0;

  const namespaceSvgIds = (section) => {
    section.querySelectorAll("svg").forEach((svg, svgIndex) => {
      if (svg.dataset.deerSvgIdsNamespaced === "true") return;

      const blockRoot = svg.closest("[id]");
      const namespace = (blockRoot?.id || `svg-${svgIndex}`).replace(
        /[^a-zA-Z0-9_-]/g,
        "-",
      );
      const elementsWithIds = [svg, ...svg.querySelectorAll("[id]")].filter(
        (element) => element.id,
      );
      const idMap = new Map();

      elementsWithIds.forEach((element) => {
        const oldId = element.id;
        const newId = `${oldId}-${namespace}`;
        idMap.set(oldId, newId);
        element.id = newId;
      });

      if (idMap.size) {
        [svg, ...svg.querySelectorAll("*")].forEach((element) => {
          Array.from(element.attributes).forEach((attribute) => {
            let value = attribute.value;

            if (
              attribute.name === "aria-labelledby" ||
              attribute.name === "aria-describedby"
            ) {
              value = value
                .split(/\s+/)
                .map((id) => idMap.get(id) || id)
                .join(" ");
            } else {
              idMap.forEach((newId, oldId) => {
                if (value === `#${oldId}`) {
                  value = `#${newId}`;
                  return;
                }

                value = value
                  .replaceAll(`url(#${oldId})`, `url(#${newId})`)
                  .replaceAll(
                    `url("#${oldId}")`,
                    `url("#${newId}")`,
                  )
                  .replaceAll(
                    `url('#${oldId}')`,
                    `url('#${newId}')`,
                  );
              });
            }

            if (value !== attribute.value) attribute.value = value;
          });
        });
      }

      svg.dataset.deerSvgIdsNamespaced = "true";
    });
  };

  const getNumberedElements = (section, pattern) =>
    Array.from(section.querySelectorAll("[handle]")).flatMap((element) => {
      const match = element.getAttribute("handle")?.match(pattern);
      return match ? [{ element, index: Number(match[1]) }] : [];
    });

  const initializeSection = (section) => {
    namespaceSvgIds(section);

    const cards = getNumberedElements(section, CARD_HANDLE_PATTERN);
    const buttons = getNumberedElements(section, BUTTON_HANDLE_PATTERN);

    if (!cards.length || !buttons.length) return null;

    const cardByIndex = new Map(cards.map(({ element, index }) => [index, element]));
    const initialIndex = cardByIndex.has(DEFAULT_CARD_INDEX)
      ? DEFAULT_CARD_INDEX
      : cards[0].index;
    const cardStates = new Map(
      cards.map(({ element }) => [
        element,
        {
          ariaHidden: element.getAttribute("aria-hidden"),
          hidden: element.hidden,
        },
      ]),
    );

    const setActive = (requestedIndex, activeButtonIndex = null) => {
      const activeIndex = cardByIndex.has(requestedIndex)
        ? requestedIndex
        : initialIndex;

      cards.forEach(({ element, index: cardIndex }) => {
        const isActive = cardIndex === activeIndex;
        element.hidden = !isActive;
        element.setAttribute(ACTIVE_ATTRIBUTE, String(isActive));
        element.setAttribute("aria-hidden", String(!isActive));
      });

      buttons.forEach(({ element, index: buttonIndex }) => {
        element.setAttribute(
          BUTTON_ACTIVE_ATTRIBUTE,
          String(buttonIndex === activeButtonIndex),
        );
      });
    };

    const cleanups = buttons.map(({ element, index }) => {
      const targetIndex = cardByIndex.has(index) ? index : initialIndex;
      const showRelatedCard = () => setActive(targetIndex, index);
      const handleClick = (event) => {
        event.preventDefault();
        setActive(targetIndex, index);
      };

      element.addEventListener("mouseenter", showRelatedCard);
      element.addEventListener("click", handleClick);

      return () => {
        element.removeEventListener("mouseenter", showRelatedCard);
        element.removeEventListener("click", handleClick);
      };
    });

    section.setAttribute(READY_ATTRIBUTE, "true");
    setActive(initialIndex);

    return () => {
      cleanups.forEach((cleanup) => cleanup());
      cards.forEach(({ element }) => {
        const originalState = cardStates.get(element);
        element.hidden = originalState?.hidden || false;
        element.removeAttribute(ACTIVE_ATTRIBUTE);
        if (!originalState || originalState.ariaHidden === null) {
          element.removeAttribute("aria-hidden");
        } else {
          element.setAttribute("aria-hidden", originalState.ariaHidden);
        }
      });
      buttons.forEach(({ element }) => {
        element.removeAttribute(BUTTON_ACTIVE_ATTRIBUTE);
      });
      section.removeAttribute(READY_ATTRIBUTE);
    };
  };

  const initializeSections = () => {
    if (!window.matchMedia(DESKTOP_QUERY).matches) return false;

    const sections = Array.from(document.querySelectorAll(SECTION_SELECTOR));
    if (!sections.length) return false;

    sections.forEach((section) => {
      if (cleanupSections.has(section)) return;
      const cleanup = initializeSection(section);
      if (cleanup) cleanupSections.set(section, cleanup);
    });

    return sections.every((section) => cleanupSections.has(section));
  };
  const scheduleInitialize = () => {
    window.clearTimeout(retryTimer);
    if (!window.matchMedia(DESKTOP_QUERY).matches) return;

    if (initializeSections()) return;
    retryAttempts += 1;
    if (retryAttempts > 200) return;
    retryTimer = window.setTimeout(scheduleInitialize, 50);
  };

  const reinitialize = () => {
    window.clearTimeout(retryTimer);
    cleanupSections.forEach((cleanup) => cleanup());
    cleanupSections.clear();
    retryAttempts = 0;
    scheduleInitialize();
  };

  const handleViewportChange = () => {
    if (window.matchMedia(DESKTOP_QUERY).matches) {
      reinitialize();
    } else {
      window.clearTimeout(retryTimer);
      cleanupSections.forEach((cleanup) => cleanup());
      cleanupSections.clear();
    }
  };
  const mediaQuery = window.matchMedia(DESKTOP_QUERY);
  mediaQuery.addEventListener?.("change", handleViewportChange);
  document.addEventListener("shopify:section:load", reinitialize);
  document.addEventListener("shopify:section:unload", reinitialize);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleInitialize, {
      once: true,
    });
  } else {
    scheduleInitialize();
  }
})();

(() => {
  const MOBILE_QUERY = "(max-width: 767px)";
  const SECTION_SELECTOR = '[handle="section3"], [handle="section8"]';
  const CARD_HANDLE_PATTERN = /^container-card-(\d+)$/i;
  const BUTTON_HANDLE_PATTERN = /^related-button-(\d+)$/i;
  const CARD_CONTAINER_SELECTOR = '[handle="Card"]';
  const PICTURE_SELECTOR = '[handle="Picture"]';
  const READY_ATTRIBUTE = "data-deer-mobile-card-switcher-ready";
  const ACTIVE_ATTRIBUTE = "data-deer-mobile-card-active";
  const CLOSE_BUTTON_CLASS = "deer-mobile-card-close";
  const CLOSE_BUTTON_ATTRIBUTE = "data-deer-mobile-card-close";

  const cleanupSectionsMobile = new Map();
  let retryTimer = null;
  let retryAttempts = 0;

  const getNumberedElements = (root, pattern) => {
    const elementsByIndex = new Map();

    root.querySelectorAll("[handle]").forEach((element) => {
      const match = element.getAttribute("handle")?.match(pattern);
      if (!match) return;

      const index = Number(match[1]);
      if (!elementsByIndex.has(index)) {
        elementsByIndex.set(index, { element, index });
      }
    });

    return Array.from(elementsByIndex.values());
  };

  const getBackgroundUrls = (value) =>
    Array.from(
      String(value || "").matchAll(
        /url\(\s*(?:"([^"]+)"|'([^']+)'|([^)]*?))\s*\)/g,
      ),
      (match) => (match[1] || match[2] || match[3] || "").trim(),
    ).filter(Boolean);

  const preloadCardMedia = (cards) => {
    const preloaders = [];

    cards.forEach(({ element }) => {
      const computedStyle = window.getComputedStyle(element);
      const sources = new Set([
        ...getBackgroundUrls(
          element.style.getPropertyValue("--bg-image-mb") ||
            computedStyle.getPropertyValue("--bg-image-mb"),
        ),
        ...getBackgroundUrls(computedStyle.backgroundImage),
      ]);

      element.querySelectorAll("img").forEach((image) => {
        image.loading = "eager";
        const source = image.currentSrc || image.src;
        if (source) sources.add(source);
      });

      sources.forEach((source) => {
        const image = new Image();
        image.decoding = "async";
        image.src = source;
        preloaders.push(image);
      });
    });

    return preloaders;
  };

  const initializeSectionMobile = (section) => {
    const picture = section.querySelector(PICTURE_SELECTOR);
    const cardContainer = section.querySelector(CARD_CONTAINER_SELECTOR);
    if (!picture || !cardContainer) return null;

    const cards = getNumberedElements(cardContainer, CARD_HANDLE_PATTERN);
    const buttons = getNumberedElements(picture, BUTTON_HANDLE_PATTERN);
    if (!cards.length || !buttons.length) return null;

    const cardByIndex = new Map(cards.map(({ element, index }) => [index, element]));
    const initialIndex = cardByIndex.has(1) ? 1 : cards[0].index;
    const cardMediaPreloads = preloadCardMedia(cards);
    let activeIndex = null;
    let activeButtonIndex = null;
    const cardStates = new Map(
      cards.map(({ element }) => [
        element,
        {
          hidden: element.hidden,
          active: element.getAttribute(ACTIVE_ATTRIBUTE),
          ariaHidden: element.getAttribute("aria-hidden"),
        },
      ]),
    );

    const setActive = (requestedIndex, requestedButtonIndex = null) => {
      activeIndex =
        requestedIndex === null
          ? null
          : cardByIndex.has(requestedIndex)
            ? requestedIndex
            : initialIndex;
      activeButtonIndex =
        activeIndex === null ? null : requestedButtonIndex;

      cards.forEach(({ element, index: cardIndex }) => {
        const isActive = cardIndex === activeIndex;
        element.hidden = !isActive;
        element.setAttribute(ACTIVE_ATTRIBUTE, String(isActive));
        element.setAttribute("aria-hidden", String(!isActive));
      });

      buttons.forEach(({ element, index: buttonIndex }) => {
        element.setAttribute(
          "data-deer-mobile-related-button-active",
          String(buttonIndex === activeButtonIndex),
        );
      });
    };

    const cleanups = buttons.map(({ element, index }) => {
      const targetIndex = cardByIndex.has(index) ? index : initialIndex;
      const handleClick = (event) => {
        event.preventDefault();
        setActive(targetIndex, index);
      };

      element.addEventListener("click", handleClick);
      element.setAttribute("data-deer-mobile-related-button", String(index));

      return () => {
        element.removeEventListener("click", handleClick);
        element.removeAttribute("data-deer-mobile-related-button");
        element.removeAttribute("data-deer-mobile-related-button-active");
      };
    });

    const closeButtonCleanups = cards.map(({ element, index }) => {
      const closeButton = document.createElement("button");
      closeButton.type = "button";
      closeButton.className = CLOSE_BUTTON_CLASS;
      closeButton.setAttribute(CLOSE_BUTTON_ATTRIBUTE, String(index));
      closeButton.setAttribute("aria-label", "Close card");
      closeButton.innerHTML = "&times;";

      const handleClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setActive(null);
      };

      closeButton.addEventListener("click", handleClick);
      element.append(closeButton);

      return () => {
        closeButton.removeEventListener("click", handleClick);
        closeButton.remove();
      };
    });

    const handleOutsideClick = (event) => {
      if (activeIndex === null) return;

      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[handle^="related-button-" i]')) return;

      const activeCard = cardByIndex.get(activeIndex);
      if (!activeCard) return;
      if (target === activeCard) {
        setActive(null);
        return;
      }
      if (activeCard.contains(target)) return;

      setActive(null);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setActive(null);
    };

    document.addEventListener("click", handleOutsideClick);
    section.addEventListener("keydown", handleEscape);

    section.setAttribute(READY_ATTRIBUTE, "true");
    setActive(null);

    return () => {
      cleanups.forEach((cleanup) => cleanup());
      closeButtonCleanups.forEach((cleanup) => cleanup());
      document.removeEventListener("click", handleOutsideClick);
      section.removeEventListener("keydown", handleEscape);
      cards.forEach(({ element }) => {
        const originalState = cardStates.get(element);
        element.hidden = originalState?.hidden || false;
        if (originalState?.active === null || originalState?.active === undefined) {
          element.removeAttribute(ACTIVE_ATTRIBUTE);
        } else {
          element.setAttribute(ACTIVE_ATTRIBUTE, originalState.active);
        }
        if (originalState?.ariaHidden === null || originalState?.ariaHidden === undefined) {
          element.removeAttribute("aria-hidden");
        } else {
          element.setAttribute("aria-hidden", originalState.ariaHidden);
        }
      });
      section.removeAttribute(READY_ATTRIBUTE);
      activeIndex = null;
      activeButtonIndex = null;
      cardMediaPreloads.length = 0;
    };
  };

  const initializeSectionsMobile = () => {
    if (!window.matchMedia(MOBILE_QUERY).matches) return false;

    const sections = Array.from(document.querySelectorAll(SECTION_SELECTOR));
    if (!sections.length) return false;

    sections.forEach((section) => {
      if (cleanupSectionsMobile.has(section)) return;
      const cleanup = initializeSectionMobile(section);
      if (cleanup) cleanupSectionsMobile.set(section, cleanup);
    });

    return sections.every((section) => cleanupSectionsMobile.has(section));
  };
  const scheduleInitialize = () => {
    window.clearTimeout(retryTimer);
    if (!window.matchMedia(MOBILE_QUERY).matches) return;

    if (initializeSectionsMobile()) return;
    retryAttempts += 1;
    if (retryAttempts > 200) return;
    retryTimer = window.setTimeout(scheduleInitialize, 50);
  };

  const reinitialize = () => {
    window.clearTimeout(retryTimer);
    cleanupSectionsMobile.forEach((cleanup) => cleanup());
    cleanupSectionsMobile.clear();
    retryAttempts = 0;
    scheduleInitialize();
  };

  const handleViewportChange = () => {
    if (window.matchMedia(MOBILE_QUERY).matches) {
      reinitialize();
    } else {
      window.clearTimeout(retryTimer);
      cleanupSectionsMobile.forEach((cleanup) => cleanup());
      cleanupSectionsMobile.clear();
    }
  };
  const mediaQuery = window.matchMedia(MOBILE_QUERY);
  mediaQuery.addEventListener?.("change", handleViewportChange);
  document.addEventListener("shopify:section:load", reinitialize);
  document.addEventListener("shopify:section:unload", reinitialize);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleInitialize, {
      once: true,
    });
  } else {
    scheduleInitialize();
  }
})();

(() => {
  const IMAGE_SELECTOR =
    '[handle="section2"] [handle="container-code"] [handle="container-image"] [handle^="image-" i]';
  const READY_ATTRIBUTE = "data-deer-copy-ready";
  const TOAST_ID = "deer-copy-toast";
  let toastTimer = null;

  const getCopyValue = (imageBlock) => {
    const handle = imageBlock.getAttribute("handle") || "";
    const match = handle.match(/^image-(.+)$/i);
    return match?.[1]?.trim() || "";
  };

  const copyText = async (value) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const helper = document.createElement("textarea");
    helper.value = value;
    helper.setAttribute("readonly", "");
    helper.style.cssText =
      "position: fixed; top: -9999px; left: -9999px; opacity: 0;";
    document.body.appendChild(helper);
    helper.focus();
    helper.select();

    const copied = document.execCommand("copy");
    helper.remove();
    if (!copied) throw new Error("Copy command failed");
  };

  const showCopyToast = () => {
    const message = "Code copied! Don"+String.fromCharCode(39)+"t forget to use it at checkout.";
    if (typeof window.toast === "function" && document.querySelector(".toast-wrapper")) {
      window.toast(message);
      return;
    }


    let toast = document.getElementById(TOAST_ID);
    if (!toast) {
      toast = document.createElement("div");
      toast.id = TOAST_ID;
      toast.className = "deer-copy-toast";
      toast.setAttribute("role", "status");
      toast.setAttribute("aria-live", "polite");
      document.body.appendChild(toast);
    }

    toast.textContent = message;
    toast.classList.add("is-visible");
    toast.style.cssText = "background: #0d4023 !important; bottom: 24px !important; box-sizing: border-box !important; color: #fff !important; display: block !important; font-size: 14px !important; left: 50% !important; line-height: 1.4 !important; max-width: calc(100vw - 32px) !important; padding: 10px 16px !important; pointer-events: none !important; position: fixed !important; text-align: center !important; transform: translate(-50%, 0) !important; transition: opacity 180ms ease, transform 180ms ease !important; visibility: visible !important; z-index: 2147483647 !important; opacity: 1 !important;";
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => {
      toast.classList.remove("is-visible");
      toast.style.setProperty("opacity", "0", "important");
      toast.style.setProperty("transform", "translate(-50%, 12px)", "important");
      toast.style.setProperty("visibility", "hidden", "important");
    }, 1800);
  };

  const copyFromImage = async (imageBlock) => {
    const value = getCopyValue(imageBlock);
    if (!value) return;

    try {
      await copyText(value);
      showCopyToast(value);
    } catch (error) {
      console.error("Deer hunting coupon copy error:", error);
    }
  };

  const initializeCopyImages = () => {
    document.querySelectorAll(IMAGE_SELECTOR).forEach((imageBlock) => {
      if (imageBlock.hasAttribute(READY_ATTRIBUTE)) return;

      const image = imageBlock.querySelector("img");
      const value = getCopyValue(imageBlock);
      if (!image || !value) return;

      imageBlock.setAttribute(READY_ATTRIBUTE, "true");
      imageBlock.setAttribute("role", "button");
      imageBlock.setAttribute("tabindex", "0");
      imageBlock.setAttribute("aria-label", `Copy ${value}`);

      image.addEventListener("click", () => copyFromImage(imageBlock));
      imageBlock.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        copyFromImage(imageBlock);
      });
    });
  };

  document.addEventListener("shopify:section:load", initializeCopyImages);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeCopyImages, {
      once: true,
    });
  } else {
    initializeCopyImages();
  }
})();

(() => {
  const VIDEO_CONTAINER_SELECTOR = '[handle="container-video" i]';
  const VIDEO_SELECTOR = ".container-bg-video";
  const CONTROLS_CLASS = "container-video-controls";
  const READY_ATTRIBUTE = "data-deer-video-controls-ready";

  const PLAY_ICON =
    '<svg aria-hidden="true" viewBox="0 0 24 24" focusable="false"><path d="M8 5v14l11-7z" fill="currentColor"></path></svg>';
  const PAUSE_ICON =
    '<svg aria-hidden="true" viewBox="0 0 24 24" focusable="false"><path d="M6 5h4v14H6zM14 5h4v14h-4z" fill="currentColor"></path></svg>';
  const VOLUME_ICON =
    '<svg aria-hidden="true" viewBox="0 0 24 24" focusable="false"><path d="M11 5 6 9H3v6h3l5 4V5Z" fill="none" stroke="currentColor" stroke-linejoin="round" stroke-width="2"></path><path d="M15.5 8.5a5 5 0 0 1 0 7M19 5a10 10 0 0 1 0 14" fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="2"></path></svg>';
  const MUTED_ICON =
    '<svg aria-hidden="true" viewBox="0 0 24 24" focusable="false"><path d="M11 5 6 9H3v6h3l5 4V5Z" fill="none" stroke="currentColor" stroke-linejoin="round" stroke-width="2"></path><path d="m16 9 5 6m0-6-5 6" fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="2"></path></svg>';

  const findControls = (container) =>
    container.querySelector(`.${CONTROLS_CLASS}`);

  const updateControls = (video, controls) => {
    const playButton = controls.querySelector("[data-container-video-play]");
    const muteButton = controls.querySelector("[data-container-video-mute]");
    const progress = controls.querySelector("[data-container-video-progress]");
    if (!playButton || !muteButton || !progress) return;

    const duration = Number(video.duration);
    const currentTime = Number(video.currentTime);
    const ratio =
      Number.isFinite(duration) && duration > 0
        ? Math.max(0, Math.min(1, currentTime / duration))
        : 0;

    const progressValue = String(ratio * 1000);
    const progressPercent = `${ratio * 100}%`;
    if (progress.value !== progressValue) progress.value = progressValue;
    if (progress.style.getPropertyValue("--video-progress") !== progressPercent) {
      progress.style.setProperty("--video-progress", progressPercent);
    }

    const playbackState = video.paused ? "paused" : "playing";
    playButton.setAttribute(
      "aria-label",
      video.paused ? "Play video" : "Pause video",
    );
    if (playButton.dataset.videoState !== playbackState) {
      playButton.innerHTML = video.paused ? PLAY_ICON : PAUSE_ICON;
      playButton.dataset.videoState = playbackState;
    }

    const soundState = video.muted || video.volume === 0 ? "muted" : "audible";
    muteButton.setAttribute(
      "aria-label",
      video.muted || video.volume === 0 ? "Unmute video" : "Mute video",
    );
    if (muteButton.dataset.videoState !== soundState) {
      muteButton.innerHTML = soundState === "muted" ? MUTED_ICON : VOLUME_ICON;
      muteButton.dataset.videoState = soundState;
    }
  };

  const removeControls = (container) => {
    findControls(container)?.remove();
    container.removeAttribute(READY_ATTRIBUTE);
  };

  const ensureControls = (container) => {
    const video = container.querySelector(VIDEO_SELECTOR);
    if (!(video instanceof HTMLVideoElement)) {
      removeControls(container);
      return;
    }

    const existingControls = findControls(container);
    if (
      existingControls &&
      container.getAttribute(READY_ATTRIBUTE) === "true" &&
      existingControls._containerVideo === video
    ) {
      updateControls(video, existingControls);
      return;
    }

    existingControls?.remove();

    const controls = document.createElement("div");
    controls.className = CONTROLS_CLASS;
    controls.setAttribute("data-container-video-controls", "true");
    controls.setAttribute("aria-label", "Video controls");
    controls.innerHTML = `
      <button class="${CONTROLS_CLASS}__button" type="button" data-container-video-play aria-label="Play video"></button>
      <input class="${CONTROLS_CLASS}__progress" type="range" data-container-video-progress min="0" max="1000" step="1" value="0" aria-label="Video progress">
      <button class="${CONTROLS_CLASS}__button" type="button" data-container-video-mute aria-label="Unmute video"></button>
    `;
    container.append(controls);
    container.setAttribute(READY_ATTRIBUTE, "true");
    controls._containerVideo = video;

    const playButton = controls.querySelector("[data-container-video-play]");
    const muteButton = controls.querySelector("[data-container-video-mute]");
    const progress = controls.querySelector("[data-container-video-progress]");
    const stopContainerAction = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };

    playButton.addEventListener("click", (event) => {
      stopContainerAction(event);
      if (video.paused) {
        const playback = video.play();
        playback?.catch?.(() => updateControls(video, controls));
      } else {
        video.pause();
      }
    });

    muteButton.addEventListener("click", (event) => {
      stopContainerAction(event);
      video.muted = !video.muted;
      if (!video.muted && video.volume === 0) video.volume = 1;
      updateControls(video, controls);
    });

    progress.addEventListener("input", (event) => {
      stopContainerAction(event);
      const duration = Number(video.duration);
      if (!Number.isFinite(duration) || duration <= 0) return;
      video.currentTime = (Number(progress.value) / 1000) * duration;
      updateControls(video, controls);
    });

    [
      "durationchange",
      "loadedmetadata",
      "timeupdate",
      "play",
      "pause",
      "volumechange",
      "ended",
    ].forEach((eventName) => {
      video.addEventListener(eventName, () => updateControls(video, controls));
    });

    updateControls(video, controls);
  };

  const initialize = () => {
    document.querySelectorAll(VIDEO_CONTAINER_SELECTOR).forEach(ensureControls);
  };

  const start = () => {
    initialize();
    if (!document.body || typeof MutationObserver !== "function") return;

    const observer = new MutationObserver(initialize);
    observer.observe(document.body, { childList: true, subtree: true });
  };

  document.addEventListener("shopify:section:load", initialize);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})();

// Limit section6 and section9 product groups to eight items on desktop until
// requested. Mobile keeps every rendered product visible.
// The optional traffic-driving image counts as one item in the initial total.
(() => {
  const MOBILE_QUERY = "(max-width: 767px)";
  const SECTION_SELECTOR = '[handle="section6" i], [handle="section9" i]';
  const PRODUCT_CONTENT_SELECTOR = '[handle="embla-product-content" i]';
  const VIEWPORT_SELECTOR = "[data-embla-viewport]";
  const EMBLA_CONTAINER_SELECTOR = "[data-embla-container]";
  const OUTER_SLIDE_SELECTOR = "[data-embla-slide]";
  const PRODUCT_CONTAINER_SELECTOR = '[handle="container" i]';
  const NESTED_EMBLA_SELECTOR = '[handle="embla" i][data-embla]';
  const PRODUCT_SLIDE_SELECTOR = "[data-embla-slide]";
  const INSERTED_IMAGE_SELECTOR = '[handle^="Traffic-driving-card-" i]';
  const HIDDEN_PRODUCT_ATTRIBUTE = "data-deer-product-more-hidden";
  const EXPANDED_ATTRIBUTE = "data-deer-products-expanded";
  const PRODUCTS_EXPANDED_EVENT = "deer:section6-products-expanded";
  const VIEW_MORE_CLASS = "deer-section6-view-more";
  const INITIAL_ITEM_LIMIT = 8;

  const getDirectChild = (parent, selector) =>
    Array.from(parent?.children || []).find((child) =>
      child.matches(selector),
    ) || null;

  const getProductSlides = (container) =>
    Array.from(container?.children || []).filter((slide) =>
      slide.matches(PRODUCT_SLIDE_SELECTOR),
    );

  const getProductContainer = (outerSlide) => {
    const nestedEmbla = getDirectChild(outerSlide, NESTED_EMBLA_SELECTOR);
    if (nestedEmbla) {
      const nestedViewport = getDirectChild(nestedEmbla, VIEWPORT_SELECTOR);
      return getDirectChild(nestedViewport, EMBLA_CONTAINER_SELECTOR);
    }

    return (
      Array.from(outerSlide?.children || []).find(
        (child) =>
          child.matches(PRODUCT_CONTAINER_SELECTOR) &&
          getProductSlides(child).length,
      ) || null
    );
  };

  const getOuterSlides = (productContent) => {
    const viewport = getDirectChild(productContent, VIEWPORT_SELECTOR);
    const outerContainer = getDirectChild(viewport, EMBLA_CONTAINER_SELECTOR);
    return Array.from(outerContainer?.children || []).filter((slide) =>
      slide.matches(OUTER_SLIDE_SELECTOR),
    );
  };

  const getInsertedImageCount = (outerSlide) =>
    Array.from(outerSlide?.children || []).filter((image) => {
      if (!image.matches(INSERTED_IMAGE_SELECTOR)) return false;
      return window.getComputedStyle(image).display !== "none";
    }).length;

  const getViewMoreButton = (outerSlide, productContainer) =>
    [
      ...Array.from(productContainer?.children || []),
      ...Array.from(outerSlide?.children || []),
    ].find((child) => child.classList.contains(VIEW_MORE_CLASS)) || null;

  const createViewMoreButton = () => {
    const button = document.createElement("button");
    button.className = VIEW_MORE_CLASS;
    button.type = "button";
    button.textContent = "VIEW MORE";
    button.setAttribute("aria-expanded", "false");
    return button;
  };

  const expandProducts = (outerSlide, productContainer, button) => {
    outerSlide.setAttribute(EXPANDED_ATTRIBUTE, "true");
    getProductSlides(productContainer).forEach((slide) =>
      slide.removeAttribute(HIDDEN_PRODUCT_ATTRIBUTE),
    );
    button.setAttribute("aria-expanded", "true");
    button.remove();
    productContainer.dispatchEvent(
      new CustomEvent(PRODUCTS_EXPANDED_EVENT, { bubbles: true }),
    );
  };

  const initializeProductGroup = (outerSlide) => {
    const productContainer = getProductContainer(outerSlide);
    if (!productContainer) return;

    const productSlides = getProductSlides(productContainer);
    const existingButton = getViewMoreButton(outerSlide, productContainer);

    if (window.matchMedia(MOBILE_QUERY).matches) {
      productSlides.forEach((slide) =>
        slide.removeAttribute(HIDDEN_PRODUCT_ATTRIBUTE),
      );
      existingButton?.remove();
      return;
    }

    const insertedImageCount = getInsertedImageCount(outerSlide);
    const totalItemCount = productSlides.length + insertedImageCount;

    if (
      totalItemCount <= INITIAL_ITEM_LIMIT ||
      outerSlide.getAttribute(EXPANDED_ATTRIBUTE) === "true"
    ) {
      productSlides.forEach((slide) =>
        slide.removeAttribute(HIDDEN_PRODUCT_ATTRIBUTE),
      );
      existingButton?.remove();
      return;
    }

    const productLimit = Math.max(
      0,
      INITIAL_ITEM_LIMIT - insertedImageCount,
    );
    productSlides.forEach((slide, index) => {
      if (index >= productLimit) {
        slide.setAttribute(HIDDEN_PRODUCT_ATTRIBUTE, "true");
      } else {
        slide.removeAttribute(HIDDEN_PRODUCT_ATTRIBUTE);
      }
    });

    const button = existingButton || createViewMoreButton();
    if (!existingButton) {
      productContainer.append(button);

      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        expandProducts(outerSlide, productContainer, button);
      });
    }
  };

  const initialize = (root = document) => {
    const sections = root.querySelectorAll?.(SECTION_SELECTOR) || [];
    sections.forEach((section) => {
      section
        .querySelectorAll(PRODUCT_CONTENT_SELECTOR)
        .forEach((productContent) => {
          getOuterSlides(productContent).forEach(initializeProductGroup);
        });
    });
  };

  document.addEventListener("shopify:section:load", (event) => {
    initialize(event.target);
  });

  const mediaQuery = window.matchMedia(MOBILE_QUERY);
  mediaQuery.addEventListener?.("change", () => initialize());

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize(), {
      once: true,
    });
  } else {
    initialize();
  }
})();
